import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaHistory, FaPlus, FaSearch, FaCreditCard, FaCalendarCheck, FaUserCheck, FaReceipt, FaEdit } from 'react-icons/fa';

interface AuditItem {
  id: number;
  user_id: number;
  member_name: string;
  action_type: 'JOINING' | 'RENEWAL' | 'PAYMENT' | 'STATUS_CHANGE';
  joining_date?: string;
  renewal_date: string;
  next_expiry_date: string;
  amount_paid: number;
  payment_mode: string;
  payment_reference?: string;
  notes?: string;
  created_at: string;
  email?: string;
  number?: string;
  company_name?: string;
}

interface Member {
  id: number;
  name: string;
  email?: string;
  company_name?: string;
  Time?: string;
}

interface MembershipAuditTrailProps {
  selectedUserId?: number | null;
  selectedUserName?: string | null;
}

const MembershipAuditTrail: React.FC<MembershipAuditTrailProps> = ({ selectedUserId, selectedUserName }) => {
  const [auditLogs, setAuditLogs] = useState<AuditItem[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');

  // Renewal / Edit Modal State
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [renewForm, setRenewForm] = useState({
    user_id: selectedUserId ? String(selectedUserId) : '',
    action_type: 'RENEWAL',
    joining_date: new Date().toISOString().split('T')[0],
    renewal_date: new Date().toISOString().split('T')[0],
    next_expiry_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    amount_paid: '15000',
    payment_mode: 'UPI',
    payment_reference: '',
    notes: 'Annual Membership Tenure Renewal'
  });
  const [isUploadingProof, setIsUploadingProof] = useState(false);

  const handleEditClick = (item: AuditItem) => {
    setEditingLogId(item.id);
    setRenewForm({
      user_id: String(item.user_id),
      action_type: item.action_type || 'RENEWAL',
      joining_date: item.joining_date ? new Date(item.joining_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      renewal_date: item.renewal_date ? new Date(item.renewal_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      next_expiry_date: item.next_expiry_date ? new Date(item.next_expiry_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      amount_paid: String(item.amount_paid || '0'),
      payment_mode: item.payment_mode || 'UPI',
      payment_reference: item.payment_reference || '',
      notes: item.notes || ''
    });
    setShowRenewModal(true);
  };

  const handleOpenNewModal = () => {
    setEditingLogId(null);
    setRenewForm({
      user_id: selectedUserId ? String(selectedUserId) : '',
      action_type: 'RENEWAL',
      joining_date: new Date().toISOString().split('T')[0],
      renewal_date: new Date().toISOString().split('T')[0],
      next_expiry_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      amount_paid: '15000',
      payment_mode: 'UPI',
      payment_reference: '',
      notes: 'Annual Membership Tenure Renewal'
    });
    setShowRenewModal(true);
  };

  const handlePaymentProofUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    setIsUploadingProof(true);

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data && res.data.imagePath) {
        setRenewForm((prev: any) => ({
          ...prev,
          payment_reference: prev.payment_reference 
            ? `${prev.payment_reference} (Proof: ${res.data.imagePath})`
            : res.data.imagePath
        }));
      }
    } catch (err) {
      console.error('Failed to upload payment proof:', err);
      alert('Failed to upload payment proof screenshot.');
    } finally {
      setIsUploadingProof(false);
    }
  };

  const fetchAuditTrail = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/membership/audit-trail', {
        params: selectedUserId ? { user_id: selectedUserId } : {}
      });
      setAuditLogs(res.data.audit_trail || []);
    } catch (err) {
      console.error('Error fetching audit trail:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      const activeMembers = list.filter((u: any) => (
        u.delete !== 1 && 
        u.role !== 'super-admin' && 
        (u.status === 'active' || !u.status)
      ));
      activeMembers.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
      setMembers(activeMembers);
    } catch (err) {
      console.error('Error fetching members in MembershipAuditTrail:', err);
    }
  };

  useEffect(() => {
    fetchAuditTrail();
    fetchMembers();
  }, [selectedUserId]);

  const handleMemberSelect = (userId: string) => {
    const selected = members.find(m => String(m.id) === userId);
    if (selected) {
      const joinDate = selected.Time ? new Date(selected.Time).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const today = new Date().toISOString().split('T')[0];
      const nextExp = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0];

      setRenewForm(prev => ({
        ...prev,
        user_id: userId,
        joining_date: joinDate,
        renewal_date: today,
        next_expiry_date: nextExp
      }));
    } else {
      setRenewForm(prev => ({ ...prev, user_id: userId }));
    }
  };

  const handleProcessRenewal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!renewForm.user_id) {
      alert('Please select a member.');
      return;
    }

    try {
      if (editingLogId) {
        await axios.put(`https://rmbbackend.rmbjaipur.com/auth/membership/audit-trail/${editingLogId}`, renewForm);
        alert('Membership audit entry updated successfully!');
      } else {
        await axios.post('https://rmbbackend.rmbjaipur.com/auth/membership/renew', renewForm);
        alert('Membership renewal processed and payment recorded in audit trail successfully!');
      }
      setShowRenewModal(false);
      setEditingLogId(null);
      fetchAuditTrail();
    } catch (err) {
      console.error('Error saving renewal/audit entry:', err);
      alert('Failed to save audit record.');
    }
  };

  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch = log.member_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (log.payment_reference && log.payment_reference.toLowerCase().includes(searchQuery.toLowerCase())) ||
                          (log.company_name && log.company_name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesAction = actionFilter === 'ALL' || log.action_type === actionFilter;
    return matchesSearch && matchesAction;
  });

  const getActionBadge = (type: string) => {
    switch (type) {
      case 'JOINING':
        return <span className="bg-blue-100 text-blue-800 border border-blue-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1"><FaUserCheck /> Initial Joining</span>;
      case 'RENEWAL':
        return <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1"><FaCalendarCheck /> Tenure Renewal</span>;
      case 'PAYMENT':
        return <span className="bg-purple-100 text-purple-800 border border-purple-300 text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1"><FaCreditCard /> Fee Payment</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 text-xs px-2.5 py-1 rounded-full font-bold">{type}</span>;
    }
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-5 rounded-xl shadow-sm border mb-5 gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaHistory className="text-[#0A2451]" /> Membership Audit Trail & Payment Ledger
            {selectedUserName && (
              <span className="bg-[#02256D] text-white text-xs px-3 py-1 rounded-full font-black ml-2 shadow">
                👤 Member: {selectedUserName}
              </span>
            )}
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Complete historical audit trail of member joining dates, annual renewals, expiry dates, payment modes, and receipt references.
          </p>
        </div>

        <button
          onClick={handleOpenNewModal}
          className="px-4 py-2 bg-[#FDBC12] hover:bg-amber-500 text-[#0A2451] font-bold rounded-lg shadow transition flex items-center gap-2 text-xs"
        >
          <FaPlus /> Process Renewal / Record Payment
        </button>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white p-4 rounded-xl shadow-sm border mb-5 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-2">Action Filter:</span>
          {['ALL', 'RENEWAL', 'JOINING', 'PAYMENT'].map((act) => (
            <button
              key={act}
              onClick={() => setActionFilter(act)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                actionFilter === act
                  ? 'bg-[#0A2451] text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {act === 'ALL' ? 'All Audit History' : act === 'RENEWAL' ? 'Renewals' : act === 'JOINING' ? 'Initial Joining' : 'Payments'}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-72">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search member or receipt..."
            className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500"
          />
          <FaSearch className="absolute left-3 top-2.5 text-gray-400 text-xs" />
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead className="bg-gray-100 text-gray-700 font-bold border-b uppercase">
              <tr>
                <th className="p-3">Audit Date</th>
                <th className="p-3">Member Name</th>
                <th className="p-3">Action Type</th>
                <th className="p-3">Joining Date</th>
                <th className="p-3">Renewal Date</th>
                <th className="p-3">Next Expiry Date</th>
                <th className="p-3">Amount Paid</th>
                <th className="p-3">Payment Mode & Ref</th>
                <th className="p-3">Notes</th>
                <th className="p-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={10} className="p-6 text-center text-gray-500 font-medium">Loading membership audit history...</td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-6 text-center text-gray-500 font-medium">No membership audit records found.</td>
                </tr>
              ) : (
                filteredLogs.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition">
                    <td className="p-3 whitespace-nowrap text-gray-500 font-mono text-[11px]">
                      {new Date(item.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-3 font-bold text-gray-900">
                      <div>{item.member_name}</div>
                      <div className="text-[10px] text-gray-500 font-normal">{item.company_name || item.email}</div>
                    </td>
                    <td className="p-3">{getActionBadge(item.action_type)}</td>
                    <td className="p-3 text-gray-600 whitespace-nowrap">{item.joining_date ? new Date(item.joining_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</td>
                    <td className="p-3 font-semibold text-emerald-800 whitespace-nowrap">{new Date(item.renewal_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td className="p-3 font-bold text-amber-800 whitespace-nowrap">{new Date(item.next_expiry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td className="p-3 font-black text-gray-900">
                      ₹{Number(item.amount_paid).toLocaleString()}
                    </td>
                    <td className="p-3">
                      <div className="font-semibold text-blue-900">{item.payment_mode}</div>
                      {item.payment_reference && (
                        <div className="text-[10px] text-gray-500 font-mono flex flex-col gap-0.5 mt-0.5">
                          <span className="flex items-center gap-1"><FaReceipt className="text-gray-400" /> Ref: {item.payment_reference}</span>
                          {(item.payment_reference.includes('uploads/') || item.payment_reference.startsWith('http')) && (
                            <a
                              href={
                                item.payment_reference.startsWith('http')
                                  ? item.payment_reference
                                  : `https://rmbbackend.rmbjaipur.com/${item.payment_reference.includes('uploads/') ? 'uploads/' + item.payment_reference.split('uploads/')[1].replace(')', '') : item.payment_reference}`
                              }
                              target="_blank"
                              rel="noreferrer"
                              className="text-[10px] text-blue-600 font-bold underline hover:text-blue-800 flex items-center gap-1 mt-0.5"
                            >
                              🖼️ View Payment Screenshot
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-gray-600 max-w-xs truncate">{item.notes || '-'}</td>
                    <td className="p-3 text-center whitespace-nowrap">
                      <button
                        onClick={() => handleEditClick(item)}
                        className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-md border border-amber-300 font-bold text-[11px] inline-flex items-center gap-1 shadow-sm transition"
                        title="Edit Audit Entry"
                      >
                        <FaEdit className="text-amber-600" /> Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Process Renewal / Edit Modal */}
      {showRenewModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border relative">
            <h3 className="text-lg font-bold text-gray-800 border-b pb-3 mb-4 flex items-center gap-2">
              <FaCalendarCheck className="text-[#0A2451]" /> {editingLogId ? '✏️ Edit Membership Audit & Ledger Record' : 'Process Member Renewal & Ledger Entry'}
            </h3>

            <form onSubmit={handleProcessRenewal} className="space-y-4 text-xs">
              <div>
                <label className="font-semibold text-gray-700 block mb-1">Select Member to Renew *</label>
                <select
                  value={renewForm.user_id}
                  onChange={(e) => handleMemberSelect(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold text-sm"
                  required
                >
                  <option value="">Choose Member</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} {m.company_name ? `(${m.company_name})` : ''}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-gray-700 block mb-1">Action Type</label>
                  <select
                    value={renewForm.action_type}
                    onChange={(e) => setRenewForm({ ...renewForm, action_type: e.target.value as any })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                  >
                    <option value="RENEWAL">Tenure Renewal</option>
                    <option value="JOINING">Initial Membership Joining</option>
                    <option value="PAYMENT">Membership Fee Payment</option>
                  </select>
                </div>
                <div>
                  <label className="font-semibold text-gray-700 block mb-1">Initial Joining Date</label>
                  <input
                    type="date"
                    value={renewForm.joining_date}
                    onChange={(e) => setRenewForm({ ...renewForm, joining_date: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-gray-700 block mb-1">Renewal Date *</label>
                  <input
                    type="date"
                    value={renewForm.renewal_date}
                    onChange={(e) => setRenewForm({ ...renewForm, renewal_date: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                    required
                  />
                </div>
                <div>
                  <label className="font-semibold text-gray-700 block mb-1">Next Expiry Date *</label>
                  <input
                    type="date"
                    value={renewForm.next_expiry_date}
                    onChange={(e) => setRenewForm({ ...renewForm, next_expiry_date: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold text-emerald-800"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-gray-700 block mb-1">Amount Paid (₹)</label>
                  <input
                    type="number"
                    value={renewForm.amount_paid}
                    onChange={(e) => setRenewForm({ ...renewForm, amount_paid: e.target.value })}
                    placeholder="15000"
                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none font-bold"
                  />
                </div>
                <div>
                  <label className="font-semibold text-gray-700 block mb-1">Payment Mode</label>
                  <select
                    value={renewForm.payment_mode}
                    onChange={(e) => setRenewForm({ ...renewForm, payment_mode: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                  >
                    <option value="UPI">UPI / Online Transfer</option>
                    <option value="Cash">Cash Receipt</option>
                    <option value="Cheque">Cheque Deposit</option>
                    <option value="Bank Transfer">Bank NEFT/RTGS</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-semibold text-gray-700 block mb-1">Payment Reference / Transaction ID / Receipt No.</label>
                <input
                  type="text"
                  value={renewForm.payment_reference}
                  onChange={(e) => setRenewForm({ ...renewForm, payment_reference: e.target.value })}
                  placeholder="e.g. UPR12903848123 or Receipt #402"
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                />
              </div>

              {/* Payment Proof / Screenshot Upload */}
              <div className="bg-emerald-50/80 p-3 rounded-lg border border-emerald-200 space-y-2">
                <label className="font-bold text-emerald-900 block flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1">🖼️ Upload Payment Screenshot / Proof (Optional)</span>
                  {isUploadingProof && <span className="text-amber-600 font-bold animate-pulse text-[11px]">Uploading screenshot...</span>}
                </label>
                
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePaymentProofUpload}
                  className="w-full text-xs text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-emerald-700 file:text-white hover:file:bg-emerald-800 cursor-pointer"
                />

                {renewForm.payment_reference && (renewForm.payment_reference.includes('uploads/') || renewForm.payment_reference.startsWith('http')) && (
                  <div className="mt-1 flex items-center gap-2 bg-emerald-100/90 p-2 rounded border border-emerald-300">
                    <img
                      src={
                        renewForm.payment_reference.startsWith('http')
                          ? renewForm.payment_reference
                          : `https://rmbbackend.rmbjaipur.com/${renewForm.payment_reference.includes('uploads/') ? 'uploads/' + renewForm.payment_reference.split('uploads/')[1].replace(')', '') : renewForm.payment_reference}`
                      }
                      alt="Payment Proof"
                      className="h-10 w-10 object-cover rounded border border-emerald-400"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <span className="text-[11px] text-emerald-900 font-bold">✓ Payment Proof Attached</span>
                  </div>
                )}
              </div>

              <div>
                <label className="font-semibold text-gray-700 block mb-1">Audit Ledger Notes</label>
                <textarea
                  value={renewForm.notes}
                  onChange={(e) => setRenewForm({ ...renewForm, notes: e.target.value })}
                  placeholder="Additional notes regarding payment or renewal"
                  className="w-full p-2.5 border border-gray-300 rounded-lg outline-none h-16"
                />
              </div>

              <div className="flex justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  onClick={() => setShowRenewModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-bold text-xs rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#0A2451] hover:bg-blue-900 text-white font-bold text-xs rounded-lg shadow"
                >
                  Save Audit Record & Extend Membership
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipAuditTrail;
